import { lazy, Suspense, type ComponentType } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Spinner } from '@opus/ui-components';
import { Layout } from '@/components/layout/Layout';

/**
 * Route map = the preserved menu (parity contract — brief 003-000-001 §2).
 * Every page owns its SEO via useSeo; this file only maps urls → lazy chunks
 * (brief 003-000-001 §2, §6).
 */
function withSuspense(Page: ComponentType): ComponentType {
  return function RoutedPage() {
    return (
      <Suspense fallback={<div className="flex justify-center py-24"><Spinner /></div>}>
        <Page />
      </Suspense>
    );
  };
}

const Home = withSuspense(lazy(() => import('@/pages/home')));
const Art = withSuspense(lazy(() => import('@/pages/art')));
const ArtDetail = withSuspense(lazy(() => import('@/pages/art-detail')));
const Code = withSuspense(lazy(() => import('@/pages/code')));
const CodeDetail = withSuspense(lazy(() => import('@/pages/code-detail')));
const Music = withSuspense(lazy(() => import('@/pages/music')));
const MusicDetail = withSuspense(lazy(() => import('@/pages/music-detail')));
const Blog = withSuspense(lazy(() => import('@/pages/blog')));
const BlogPost = withSuspense(lazy(() => import('@/pages/blog').then((m) => ({ default: m.BlogPostPage }))));
const Services = withSuspense(lazy(() => import('@/pages/services')));
const About = withSuspense(lazy(() => import('@/pages/about')));
const Contact = withSuspense(lazy(() => import('@/pages/contact')));
const Interactive = withSuspense(lazy(() => import('@/pages/interactive')));
const Search = withSuspense(lazy(() => import('@/pages/search')));
const NotFound = withSuspense(lazy(() => import('@/pages/not-found')));

export default function App() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/art" element={<Art />} />
        <Route path="/art/:slug" element={<ArtDetail />} />
        <Route path="/code" element={<Code />} />
        <Route path="/code/:slug" element={<CodeDetail />} />
        <Route path="/music" element={<Music />} />
        <Route path="/music/:slug" element={<MusicDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/category/:category" element={<Blog />} />
        <Route path="/blog/tag/:tag" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/interactive" element={<Interactive />} />
        <Route path="/search" element={<Search />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}
